import {
  useState,
  useCallback,
  useEffect,
  useMemo,
  useContext,
  useLayoutEffect,
} from 'react';
import { useParams } from 'react-router-dom';
import { Flex } from 'antd';
import { useFormik } from 'formik';

import dayjs from 'dayjs';
import { MainForm, RequiredNotification } from '@Components/index';
import FormItems from '@Components/MainForm/components/FormItems/FormItems';
import {
  useCancelResignationRequestMutation,
  useDeleteResignationRequestMutation,
  useGetResignationRequestFormQuery,
  useGetSettlementAmountMutation,
  useSaveDraftResignationRequestMutation,
  useSubmitResignationRequestMutation,
} from '@Features/EmployeeSelfService/requests/ResignationRequests/store/api/resignationRequestApiSlice';
import { getReasonOptions } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/selectors/resignationRequestSelector';
import { resignationRequestActions } from '@Features/EmployeeSelfService/requests/ResignationRequests/store/resignationRequestSlice';
import {
  useAppDispatch,
  useAppSelector,
  useHasAccessToSubmit,
  useNavigationToListRequests,
  useRequisitionInfoExistingReq,
} from '@Hooks/index';
import { currentUserSelector } from '@Store/selectors/userInfoSelector';
import { FooterActions } from '@Enum/index';
import { NotificationContext } from '@Context/NotificationContext';
import { RequestError } from '@Types/api/form';
import { ResignationStatusCodes } from '@Enum/api/ResignationStatusCodes.enum';
import { getEditFormType } from '@Features/utils/getEditFormType';
import { validateForm } from '@Features/utils/validateForm';

import { initialFormValues } from '../initialValues';
import type {
  ResignationDateChangeHandler,
  ResignationRequestFormValues,
} from '../types';
import { RequestDetailsSection } from '../components';
import { ResignationRequestFormSchema } from '../validationSchema';
import { LAST_WORKING_DATE_DIFF_IN_DAYS } from '../constants';
import { prepareFormHeaderData } from '../utils';

const ResignationRequestCreate: React.FC = () => {
  const {
    editSuccessNotification,
    errorNotification,
    deleteSuccessNotification,
    submitSuccessNotification,
    cancelSuccessNotification,
  } = useContext(NotificationContext);
  const { handleBackToListRequests } = useNavigationToListRequests();
  const dispatch = useAppDispatch();
  const { requestId } = useParams();

  const [showDeleteNotification, setShowDeleteNotification] = useState(false);
  const currentUser = useAppSelector(currentUserSelector);
  const reasonOptions = useAppSelector(getReasonOptions);

  const [disabledSaveForm, setDisableSaveForm] = useState(false);

  const [cancelRequest] = useCancelResignationRequestMutation();
  const [submitRequest] = useSubmitResignationRequestMutation();
  const [saveResignationRequest] = useSaveDraftResignationRequestMutation();
  const [deleteRequest] = useDeleteResignationRequestMutation();
  const { data: editDataForm } = useGetResignationRequestFormQuery(requestId!, {
    skip: !requestId,
    refetchOnMountOrArgChange: true,
  });
  const [getSettlementAmount] = useGetSettlementAmountMutation();
  const hasAccessToSubmit = useHasAccessToSubmit('Resignation');

  useLayoutEffect(() => {
    if (!hasAccessToSubmit) {
      setDisableSaveForm(true);
    }
  }, [hasAccessToSubmit]);

  const formHeaderData = useMemo(() => {
    const output = prepareFormHeaderData(editDataForm);
    return output;
  }, [editDataForm]);
  const requisitionInfo = useRequisitionInfoExistingReq(
    formHeaderData,
    'Resignation'
  );

  useEffect(
    () => () => {
      dispatch(resignationRequestActions.setResignationRequest(null));
    },
    []
  );

  const handleSubmit = useCallback(
    (values: ResignationRequestFormValues): void => {
      const submitForm = async (): Promise<void> => {
        const body = {
          requestId: editDataForm?.requestId,
          empNumber: editDataForm?.empNumber as string,
          resDate: values.resignationDate,
          lastWrkngDate: values.lastWorkingDate,
          letterContent: values.letterOfContent,
          reason: values.reason as number,
        };

        setDisableSaveForm(true);

        try {
          const {
            createdDate,
            statusId,
            requestId: reqId,
          } = await saveResignationRequest(body).unwrap();

          dispatch(
            resignationRequestActions.setResignationRequest({
              createdDate,
              requestId: reqId,
              reqStatusId: statusId,
            })
          );

          await submitRequest(String(reqId)).unwrap();

          submitSuccessNotification(String(reqId));

          setDisableSaveForm(false);

          handleBackToListRequests();
        } catch (error) {
          const message = (error as RequestError)?.data;
          errorNotification(message);

          setDisableSaveForm(false);
        }
      };

      submitForm();
    },
    [handleBackToListRequests, editDataForm]
  );

  const formik = useFormik({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema: ResignationRequestFormSchema,
    onSubmit: handleSubmit,
  });

  const handleSave = useCallback(async (): Promise<void> => {
    const isFormValid = await validateForm(
      ResignationRequestFormSchema,
      formik
    );

    if (!isFormValid) return;

    const body = {
      requestId: editDataForm?.requestId,
      empNumber: editDataForm?.empNumber as string,
      resDate: formik.values.resignationDate,
      lastWrkngDate: formik.values.lastWorkingDate,
      letterContent: formik.values.letterOfContent,
      reason: formik.values.reason as number,
    };

    setDisableSaveForm(true);

    try {
      const {
        requestId: reqId,
        createdDate,
        statusId,
      } = await saveResignationRequest(body).unwrap();

      dispatch(
        resignationRequestActions.setResignationRequest({
          createdDate,
          requestId: reqId,
          reqStatusId: statusId,
        })
      );

      editSuccessNotification(String(reqId));

      setDisableSaveForm(false);
    } catch (error) {
      const message = (error as RequestError)?.data;
      errorNotification(message);

      setDisableSaveForm(false);
    }
  }, [editDataForm, formik, ResignationRequestFormSchema]);

  useEffect(() => {
    if (!editDataForm) {
      return;
    }

    formik.setValues({
      resignationDate: editDataForm.resDate,
      approxSettlementAmount: '',
      lastWorkingDate: editDataForm.lastWrkngDate,
      letterOfContent: editDataForm.letterContent,
      reason: editDataForm.reason,
    });

    getSettlementAmount(editDataForm.resDate)
      .unwrap()
      .then((response) => {
        formik.setFieldValue('approxSettlementAmount', response);
      })
      .catch(() => {
        formik.setFieldValue('approxSettlementAmount', '');
      });
  }, [editDataForm]);

  const onResignationDateChange: ResignationDateChangeHandler = useCallback(
    (value) => {
      if (!value) {
        formik.setFieldValue('lastWorkingDate', '');
        formik.setFieldValue('approxSettlementAmount', '');
        return;
      }

      const lastWorkingDate = dayjs(value)
        .add(LAST_WORKING_DATE_DIFF_IN_DAYS, 'days')
        .endOf('day');
      formik.setFieldValue('lastWorkingDate', lastWorkingDate);

      getSettlementAmount(dayjs(value).toISOString())
        .unwrap()
        .then((response) => {
          formik.setFieldValue('approxSettlementAmount', response);
        })
        .catch(() => {
          formik.setFieldValue('approxSettlementAmount', '');
        });
    },
    [formik]
  );

  const handleDeleteRequest = useCallback((): void => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    deleteRequest(reqId)
      .unwrap()
      .then(() => {
        deleteSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [editDataForm, handleBackToListRequests]);

  const showDeleteRequestPopup = (): void => {
    setShowDeleteNotification(true);
  };

  const handleCancel = useCallback(() => {
    if (!editDataForm?.requestId) {
      return;
    }

    const reqId = String(editDataForm.requestId);

    cancelRequest(reqId)
      .unwrap()
      .then(() => {
        cancelSuccessNotification(reqId);

        handleBackToListRequests();
      })
      .catch(({ data }) => {
        errorNotification(data);
      });
  }, [handleBackToListRequests, editDataForm]);

  const handleFooterAction = useCallback(
    (action: FooterActions) => {
      if (action === FooterActions.CANCEL) {
        handleCancel();
      }

      if (action === FooterActions.SAVE_AS_DRAFT) {
        handleSave();
      }

      if (action === FooterActions.DELETE_REQUEST) {
        showDeleteRequestPopup();
      }

      if (action === FooterActions.BACK_TO_REQUESTS) {
        handleBackToListRequests();
      }
    },
    [handleBackToListRequests, handleSave, handleCancel]
  );

  const isOpenStatus =
    !editDataForm || editDataForm.statusId === ResignationStatusCodes.OPEN;
  const formType = getEditFormType(isOpenStatus);

  return (
    <Flex gap={10} vertical style={{ height: '100%' }}>
      <MainForm
        handleSubmit={formik.handleSubmit}
        userInfo={currentUser}
        requisitionInfo={requisitionInfo}
        onFooterAction={handleFooterAction}
        disabledSaveForm={disabledSaveForm}
        formType={formType}
      >
        <FormItems>
          <RequestDetailsSection
            label="Request Details"
            formik={formik}
            formApproval={false}
            reasonOptions={reasonOptions}
            onResignationDateChange={onResignationDateChange}
          />
        </FormItems>
      </MainForm>
      <RequiredNotification
        title="Delete Request"
        message={`Are you sure you want to delete request no.${requestId}?`}
        isModalOpen={showDeleteNotification}
        textSuccessBtn="Yes, Delete"
        textCancelBtn="No, Thanks"
        handleCancel={() => setShowDeleteNotification(false)}
        handleSuccess={handleDeleteRequest}
        isDeleteNotification
      />
    </Flex>
  );
};

export default ResignationRequestCreate;
